import React from "react";
import { Link, NavLink } from "react-router-dom";
import { MapPin, Phone, Mail, Clock, Linkedin, Facebook, Youtube, Instagram } from "lucide-react";
import { useSiteAboutData, useSiteServicesData } from "../hooks/useSiteData";
import BrandLockup from "./BrandLockup";
import { getCompanyBranding } from "../lib/site";
import { splitContactNumbers } from "../lib/utils";
import { stripHtml } from "../lib/html";
import { VISIBLE_NAV_ITEMS } from "../lib/nav";

function XIcon({ className }) {
  return (
    <svg className={className} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
    </svg>
  );
}

function FooterHeading({ children }) {
  return (
    <h4 className="text-white font-bold text-sm uppercase tracking-wider mb-5 pb-2 border-b-2 border-[#1e6fcf] inline-block">
      {children}
    </h4>
  );
}

export default function Footer() {
  const { data: companyInfo = { address: "", phone: "", mobile: "", email: "", tagline: "" } } = useSiteAboutData();
  const { legalName } = getCompanyBranding(companyInfo);
  const footerBlurb =
    stripHtml(companyInfo.headerDescription) ||
    "Engineering excellence since 2004. Specialists in structural repair, waterproofing, and turnkey civil construction across India.";
  const phoneNumbers = splitContactNumbers(companyInfo.phone);
  const mobileNumbers = splitContactNumbers(companyInfo.mobile);
  const socialLinks = [
    { Icon: Linkedin, label: "LinkedIn", href: companyInfo.linkedinUrl },
    { Icon: Facebook, label: "Facebook", href: companyInfo.facebookUrl },
    { Icon: Youtube, label: "YouTube", href: companyInfo.youtubeUrl },
    { Icon: Instagram, label: "Instagram", href: companyInfo.instagramUrl },
    { Icon: XIcon, label: "X", href: companyInfo.twitterXUrl },
  ].filter((item) => Boolean(item.href));
  const { data: servicesPage } = useSiteServicesData();
  const services = servicesPage?.items || [];

  return (
    <footer className="bg-[#060f1c] text-slate-300 border-t border-[#1e6fcf]/25">
      <div className="max-w-7xl mx-auto px-6 py-14 lg:py-16">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-12 gap-10 lg:gap-8">
          <div className="sm:col-span-2 lg:col-span-3 min-w-0">
            <Link to="/" className="group inline-flex max-w-full" aria-label={`${legalName} — Home`}>
              <BrandLockup about={companyInfo} variant="dark" alwaysShowText />
            </Link>
            <p className="mt-5 text-sm leading-relaxed text-slate-400 max-w-sm">{footerBlurb}</p>
            <div className="flex gap-2.5 mt-6">
              {socialLinks.map(({ Icon, label, href }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={label}
                  className="w-9 h-9 rounded-md bg-white/5 border border-white/10 text-slate-300 hover:bg-[#1e6fcf] hover:border-[#1e6fcf] hover:text-white flex items-center justify-center transition-colors duration-200"
                >
                  <Icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          <div className="lg:col-span-2 min-w-0">
            <FooterHeading>Quick Links</FooterHeading>
            <ul className="grid grid-cols-2 gap-x-4 gap-y-2.5 text-sm">
              {VISIBLE_NAV_ITEMS.map((item) => (
                <li key={item.to}>
                  <NavLink
                    to={item.to}
                    end={item.to === "/"}
                    className={({ isActive }) =>
                      `transition-colors leading-snug ${
                        isActive ? "text-[#aad4fa] font-semibold" : "text-slate-400 hover:text-[#aad4fa]"
                      }`
                    }
                  >
                    {item.label}
                  </NavLink>
                </li>
              ))}
            </ul>
          </div>

          <div className="sm:col-span-2 lg:col-span-4">
            <FooterHeading>Our Services</FooterHeading>
            {services.length === 0 ? (
              <Link to="/services" className="text-sm text-[#aad4fa] hover:text-white transition-colors">
                View all services
              </Link>
            ) : (
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2.5 text-sm">
                {services.map((s) => (
                  <li key={s.id}>
                    <Link
                      to={`/services/${s.id}`}
                      className="text-slate-400 hover:text-[#aad4fa] transition-colors leading-snug"
                    >
                      {s.title}
                    </Link>
                  </li>
                ))}
              </ul>
            )}
          </div>

          <div className="lg:col-span-3">
            <FooterHeading>Get in Touch</FooterHeading>
            <ul className="space-y-4 text-sm">
              <li className="flex items-start gap-3">
                <MapPin className="w-4 h-4 text-[#1e6fcf] mt-0.5 flex-shrink-0" />
                <span className="text-slate-300 leading-relaxed">{companyInfo.address}</span>
              </li>
              {phoneNumbers.length > 0 && (
                <li className="flex items-start gap-3">
                  <Phone className="w-4 h-4 text-[#1e6fcf] mt-0.5 flex-shrink-0" />
                  <p className="text-slate-300 leading-relaxed">
                    {phoneNumbers.map((number, index) => (
                      <React.Fragment key={number}>
                        {index > 0 && <span className="text-slate-500">, </span>}
                        <a href={`tel:${number}`} className="hover:text-[#aad4fa] transition-colors whitespace-nowrap">
                          {number}
                        </a>
                      </React.Fragment>
                    ))}
                  </p>
                </li>
              )}
              {mobileNumbers.length > 0 && (
                <li className="flex items-start gap-3">
                  <Phone className="w-4 h-4 text-[#1e6fcf] mt-0.5 flex-shrink-0" />
                  <p className="text-slate-300 leading-relaxed">
                    {mobileNumbers.map((number, index) => (
                      <React.Fragment key={number}>
                        {index > 0 && <span className="text-slate-500">, </span>}
                        <a href={`tel:${number}`} className="hover:text-[#aad4fa] transition-colors whitespace-nowrap">
                          {number}
                        </a>
                      </React.Fragment>
                    ))}
                  </p>
                </li>
              )}
              <li className="flex items-center gap-3">
                <Mail className="w-4 h-4 text-[#1e6fcf] flex-shrink-0" />
                <a href={`mailto:${companyInfo.email}`} className="text-slate-300 hover:text-[#aad4fa] transition-colors break-all">
                  {companyInfo.email}
                </a>
              </li>
              <li className="flex items-start gap-3">
                <Clock className="w-4 h-4 text-[#1e6fcf] mt-0.5 flex-shrink-0" />
                <span className="text-slate-300 leading-relaxed">
                  {companyInfo.timing || "Mon – Sat: 9:00 AM – 6:30 PM"}
                </span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      <div className="border-t border-white/5 bg-[#040a14] text-slate-500">
        <div className="max-w-7xl mx-auto px-6 py-5 text-xs flex flex-col md:flex-row justify-between items-center gap-2">
          <p>© {new Date().getFullYear()} {legalName} All rights reserved.</p>
          <p>
            Engineered with precision. Built to last.{" "}
            <a
              href="https://pixoatic.com/"
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#aad4fa] hover:text-white transition-colors font-semibold"
            >
              Pixoatic
            </a>
          </p>
        </div>
      </div>
    </footer>
  );
}
